import { buildRiskWidgetSeed } from "@repo/shared";
import {
  usePublicRiskAssessmentValue,
  useSubmitPublicRiskAssessment,
} from "@/lib/hooks/use-public-flow/use-risk-assessment";
import { useT } from "@/lib/i18n/use-t";
import { log } from "@/lib/logger/browser-logger";
import { RiskAssessmentWidget } from "@/pages/clients/detail/flows/instance/risk-assessment-widget";
import { T } from "./risk-assessment-renderer.i18n";
import type { ComponentStyleType, GoalData } from "./types";

type PublicRiskAssessmentRendererProps = {
  component: { config?: string };
  riskModelId: string | null;
  currentGoalId: string | null;
  /** The goal being iterated — the authority on its own score (see below). */
  currentGoal: GoalData | null;
  isCompleted: boolean;
  language: string;
  hasValidationError?: boolean;
  /** QL-188 — group answering note; null for non-group clients. */
  groupNote?: string | null;
};

export function RiskAssessmentRenderer({
  component,
  riskModelId,
  currentGoalId,
  currentGoal,
  isCompleted,
  language,
  hasValidationError,
  groupNote,
}: PublicRiskAssessmentRendererProps) {
  const t = useT(T);
  const { inputData, calculatedData, isLoading } = usePublicRiskAssessmentValue(currentGoalId);
  const submitRisk = useSubmitPublicRiskAssessment();

  if (!riskModelId) {
    return (
      <div className="text-sm text-muted-foreground p-4 border border-dashed rounded-lg">
        {t.riskAssessmentNotConfigured}
      </div>
    );
  }

  let riskConfig: { style?: string; title?: string; description?: string } = {};
  try {
    riskConfig = component.config ? JSON.parse(component.config) : {};
  } catch {
    // Invalid config
  }

  // QL-212 — the stored answer seeds the widget even while it is half finished;
  // only the server's *result* waits until it is real. Shared with the advisor,
  // so a partial survives a reload on both surfaces identically.
  const { value: riskSnapshotValue } = buildRiskWidgetSeed(inputData, calculatedData);

  // The score comes from the GOAL ROW, not from the seed. A goal scored by a
  // client-level fan-out has its columns written by `applyToGoal`, which never
  // touches this goal's component-value result — so that result may still be the
  // partial one. Reading it here would leave `finalScore` null and let the widget
  // fall back to its own matrix lookup, which knows nothing about the model's
  // cap: a capped client would be shown the higher number. This mirrors the
  // advisor's goal page exactly.
  const finalScore = currentGoal?.riskScore ?? null;
  const isScoreCapped = currentGoal?.isRiskScoreCapped === true;

  const handleChange = (value: string, isComplete: boolean) => {
    if (!currentGoalId) return;
    try {
      const riskData = JSON.parse(value);
      // QL-212 — "complete" means every input section this page renders has an
      // answer; the widget decides it, because only it knows which sections the
      // model's scopes put here. The SCORE is never a client-side decision — the
      // server derives it from the risk matrix — so it does not gate
      // commit-vs-autosave either.
      submitRisk.mutate({ goalId: currentGoalId, data: riskData, isComplete, lang: language });
    } catch (error) {
      log.error({ err: error }, "Failed to parse risk assessment data");
    }
  };

  if (isLoading) return null;

  return (
    <RiskAssessmentWidget
      riskModelId={riskModelId}
      goalId={currentGoalId}
      pageType="goal"
      value={riskSnapshotValue}
      onChange={handleChange}
      disabled={isCompleted}
      finalScore={finalScore}
      isScoreCapped={isScoreCapped}
      style={(riskConfig.style as ComponentStyleType) || "border"}
      title={riskConfig.title}
      description={riskConfig.description}
      hasValidationError={hasValidationError}
      groupNote={groupNote}
    />
  );
}
